import { AnalyticsEvent, EventCategories } from "@core/analytics/analytics-event";
import { AnalyticsDimension, AnalyticsTracker } from "@core/analytics/trackers/analytics-tracker";
import { getLogger } from "@core/logging/logger";

export type GoogleAnalyticsDataLayer = {
	push: (params: GoogleAnalyticsDataLayerParams) => void;
};

export interface GoogleAnalyticsDatalayerEvent {
	event: "googleAnalyticsEvent";
	eventCategory: string;
	eventAction: string;
	eventLabel: string;
}

export interface GoogleAnalyticsDataLayerCustomPageView {
	event: "pageView";
	pageVirtualURL: string;
	pageVirtualTitle: string;
}

type GoogleAnalyticsDataLayerDictionary = { [variableName: string]: AnalyticsDimension };

export type GoogleAnalyticsDataLayerParams =
	| GoogleAnalyticsDatalayerEvent
	| GoogleAnalyticsDataLayerCustomPageView
	| GoogleAnalyticsDataLayerDictionary;

export class GoogleAnalyticsTracker extends AnalyticsTracker {
	private readonly logger = getLogger("📊️ [GA Analytics]");

	async trackEvent(e: AnalyticsEvent<EventCategories>): Promise<void> {
		const gaEvent = this.googleAnalyticsEventFromAnalyticsEvent(e);
		const success = this.push(gaEvent);
		if (success && process.env.ENVIRONMENT === "dev") {
			this.logger.debug(
				`track event (cat:${gaEvent.eventCategory}) - "${gaEvent.eventAction}", ${
					gaEvent.eventLabel ? " with params: " + gaEvent.eventLabel : ""
				}`,
			);
		}
	}

	async setCustomDimension(dimensionName: string, dimensionValue: AnalyticsDimension): Promise<void> {
		const success = this.push({ [dimensionName]: dimensionValue });
		if (success && process.env.ENVIRONMENT === "dev") {
			this.logger.debug(`set Custom dimension ${dimensionName} with value "${dimensionValue}"`);
		}
	}

	async trackView(customTitle?: string | undefined, customUrl?: string | undefined): Promise<void> {
		if (customUrl) {
			const success = this.push({
				event: "pageView",
				pageVirtualURL: customUrl,
				pageVirtualTitle: customTitle ?? "",
			});
			if (success && process.env.ENVIRONMENT === "dev") {
				this.logger.debug(`set pageView ${customTitle} with value "${customUrl}"`);
			}
		}
	}

	private push(e: GoogleAnalyticsDataLayerParams): boolean {
		if (!window.dataLayer) {
			return false;
		}
		window.dataLayer.push(e);
		return true;
	}

	private googleAnalyticsEventFromAnalyticsEvent(
		event: AnalyticsEvent<EventCategories>,
	): GoogleAnalyticsDatalayerEvent {
		return {
			event: "googleAnalyticsEvent",
			eventCategory: event.category,
			eventAction: event.action,
			eventLabel: this.buildParamsLabel(event.params),
		};
	}
}
